import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Check } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { BENEFITS } from '@/constants/content'

export const BenefitsSection: FC = (): ReactNode => {
    return (
        <section
            id='nutzen'
            aria-labelledby='nutzen-title'
            className='py-24 sm:py-28'
        >
            <div className='mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <div className='lg:sticky lg:top-28'>
                        <SectionHeading
                            titleId='nutzen-title'
                            eyebrow='Projektnutzen'
                            title='Was sich auf Ihrer Baustelle verbessert.'
                            description='ConcreTrack liefert nicht einfach mehr Daten, sondern eine belastbare Grundlage für bessere Entscheidungen im Baustellenalltag.'
                        />

                        <figure className='relative mt-10 overflow-hidden rounded-[1.75rem] border border-border bg-muted'>
                            <Image
                                src='/images/bauleitung-baustelle.jpg'
                                alt='Bauleiter mit Schutzhelm und Tablet auf einer Baustelle, im Hintergrund Turmdrehkrane'
                                width={1500}
                                height={841}
                                sizes='(min-width: 1024px) 40vw, 100vw'
                                className='aspect-[4/3] h-auto w-full object-cover object-[68%_center]'
                            />

                            <figcaption className='absolute inset-x-4 bottom-4 rounded-2xl border border-white/15 bg-ink/80 px-4 py-3 text-sm leading-snug text-ink-foreground backdrop-blur-md'>
                                Bauleitung, Auftraggeber und Qualitätssicherung arbeiten mit demselben Datenstand.
                            </figcaption>
                        </figure>
                    </div>
                </div>

                <ol className='grid gap-5 lg:col-span-7'>
                    {BENEFITS.map((benefit, index) => {
                        const Icon = benefit.icon

                        return (
                            <li
                                key={benefit.label}
                                className='reveal rounded-[1.75rem] border border-border bg-card p-6 sm:p-8'
                            >
                                <div className='flex items-center gap-3'>
                                    <span className='flex size-11 items-center justify-center rounded-2xl bg-accent text-accent-foreground'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <p className='font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground'>
                                        <span className='text-primary'>{`0${index + 1}`}</span> · {benefit.label}
                                    </p>
                                </div>

                                <h3 className='mt-6 text-balance text-2xl font-light leading-snug tracking-[-0.015em] text-foreground sm:text-[1.65rem]'>
                                    {benefit.title}
                                </h3>

                                <p className='mt-3 text-pretty leading-relaxed text-muted-foreground'>
                                    {benefit.text}
                                </p>

                                <ul className='mt-6 grid gap-2.5 border-t border-border pt-6'>
                                    {benefit.points.map((point) => (
                                        <li key={point} className='flex gap-3 text-[0.95rem] text-foreground/85'>
                                            <Check className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        )
                    })}
                </ol>
            </div>
        </section>
    )
}
